import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Link, useNavigate } from 'react-router-dom';
import VideoSection from '../components/VideoSection';
import QuoteCard from '../components/QuoteCard';

const WeddingHome: React.FC = () => {
  const navigate = useNavigate();
  const [timeTogether, setTimeTogether] = useState({
    years: 0,
    months: 0,
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0,
  });

  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);

  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  // Couple photos for carousel
  const couplePhotos = [
    { src: '/couple-photo.jpg', alt: 'Noor & Priya - Together Forever' },
    { src: '/photos/trips/gallery2.jpg', alt: 'Noor & Priya - Together Forever'  },
    { src: '/photos/trips/gallery3.jpg', alt: 'Noor & Priya - Together Forever' },
    // Add more photos as needed - these will show placeholder if not found
    { src: '/photos/pre-wedding/gallery4.jpg', alt: 'Noor & Priya - Together Forever'  },
    { src: '/photos/pre-wedding/gallery6.jpg', alt: 'Noor & Priya - Together Forever' }
  ];

  // Relationship start date: September 5, 2020 at 3 PM
  const relationshipStartDate = new Date('2020-09-05T15:00:00').getTime();

  // Scroll to top on page load
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const timer = setInterval(() => {
      const now = new Date().getTime();
      const timeElapsed = now - relationshipStartDate;

      if (timeElapsed > 0) {
        // Calculate years, months, days, hours, minutes, seconds
        const years = Math.floor(timeElapsed / (1000 * 60 * 60 * 24 * 365.25));
        const remainingAfterYears = timeElapsed % (1000 * 60 * 60 * 24 * 365.25);
        
        const months = Math.floor(remainingAfterYears / (1000 * 60 * 60 * 24 * 30.44));
        const remainingAfterMonths = remainingAfterYears % (1000 * 60 * 60 * 24 * 30.44);
        
        const days = Math.floor(remainingAfterMonths / (1000 * 60 * 60 * 24));
        const remainingAfterDays = remainingAfterMonths % (1000 * 60 * 60 * 24);
        
        const hours = Math.floor(remainingAfterDays / (1000 * 60 * 60));
        const remainingAfterHours = remainingAfterDays % (1000 * 60 * 60);
        
        const minutes = Math.floor(remainingAfterHours / (1000 * 60));
        const seconds = Math.floor((remainingAfterHours % (1000 * 60)) / 1000);

        setTimeTogether({
          years,
          months,
          days,
          hours,
          minutes,
          seconds,
        });
      }
    }, 1000);

    return () => clearInterval(timer);
  }, [relationshipStartDate]);

  // Auto-rotate carousel photos
  useEffect(() => {
    const carouselTimer = setInterval(() => {
      setCurrentPhotoIndex((prevIndex) => 
        (prevIndex + 1) % couplePhotos.length
      );
    }, 4000); // Change photo every 4 seconds

    return () => clearInterval(carouselTimer);
  }, [couplePhotos.length]);

  const scrollToStory = () => {
    window.scrollTo({
      top: window.innerHeight,
      behavior: 'smooth',
    });
  };

  const FloatingHeart: React.FC<{ delay: number; x: string; y: string }> = ({ delay, x, y }) => (
    <motion.div
      className="floating-heart text-2xl"
      style={{ left: x, top: y }}
      animate={{
        y: [-20, -40, -20],
        rotate: [-5, 5, -5],
        opacity: [0.2, 0.6, 0.2],
      }}
      transition={{
        duration: 6,
        repeat: Infinity,
        delay,
        ease: "easeInOut",
      }}
    >
      ♥
    </motion.div>
  );

  const Sparkle: React.FC<{ delay: number; x: string; y: string }> = ({ delay, x, y }) => (
    <motion.div
      className="sparkle text-lg"
      style={{ left: x, top: y }}
      animate={{
        scale: [1, 1.5, 1],
        opacity: [0.3, 1, 0.3],
        rotate: [0, 180, 360],
      }}
      transition={{
        duration: 3,
        repeat: Infinity,
        delay,
        ease: "easeInOut",
      }}
    >
      ✨
    </motion.div>
  );

  return (
    <div className="relative min-h-screen overflow-hidden">
      {/* Hero Section with Background Video */}
      <section className="pb-10 relative min-h-screen flex items-center justify-center px-4 overflow-hidden">
        {/* Background Video - Only in Hero Section */}
        <div className="absolute inset-0 z-0">
          <video
            autoPlay
            muted
            loop
            playsInline
            className="w-full h-full object-cover"
            // poster="/couple-photo.jpg"
          >
            <source src="/us.mov" type="video/mp4" />
            <source src="/us3.mov" type="video/quicktime" />
            Your browser does not support the video tag.
          </video>
          {/* Enhanced Video Overlay with gradient */}
          <div className="absolute inset-0 bg-gradient-to-br from-black/40 via-black/20 to-black/50"></div>
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
        </div>

        {/* Enhanced Background Elements */}
        <div className="absolute inset-0 z-10">
          {/* Floating Hearts */}
          {[...Array(12)].map((_, i) => (
            <FloatingHeart
              key={`heart-${i}`}
              delay={i * 0.6}
              x={`${5 + i * 8}%`}
              y={`${10 + (i % 4) * 20}%`}
            />
          ))}
          {/* Sparkles */}
          {[...Array(10)].map((_, i) => (
            <Sparkle
              key={`sparkle-${i}`}
              delay={i * 0.8}
              x={`${10 + i * 9}%`}
              y={`${15 + (i % 3) * 30}%`}
            />
          ))}
          {/* Floating Petals */}
          {[...Array(8)].map((_, i) => (
            <motion.div
              key={`petal-${i}`}
              className="absolute text-2xl opacity-60"
              style={{ left: `${20 + i * 10}%`, top: `${25 + (i % 2) * 35}%` }}
              animate={{
                y: [-30, -60, -30],
                rotate: [0, 180, 360],
                opacity: [0.3, 0.8, 0.3],
              }}
              transition={{
                duration: 8,
                repeat: Infinity,
                delay: i * 1.2,
                ease: "easeInOut",
              }}
            >
              🌸
            </motion.div>
          ))}
        </div>
        <div className="text-center max-w-7xl mx-auto relative z-20 mt-10">
          {/* Wedding Date Badge - Enhanced */}
          <motion.div
            initial={{ opacity: 0, y: -30, scale: 0.8 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="mb-12"
          >
            <div className="inline-block bg-gradient-to-r from-white/20 to-white/30 backdrop-blur-md rounded-full px-8 py-4 border border-white/50 shadow-2xl hover:shadow-3xl transition-all duration-300 mt-20">
              <p className="text-white font-bold text-xl drop-shadow-lg flex items-center gap-3">
                <motion.span
                  animate={{ rotate: [0, 10, -10, 0] }}
                  transition={{ duration: 2, repeat: Infinity, delay: 0.5 }}
                >
                  💍
                </motion.span>
                January 4, 2026
                <motion.span
                  animate={{ rotate: [0, -10, 10, 0] }}
                  transition={{ duration: 2, repeat: Infinity, delay: 0.5 }}
                >
                  💍
                </motion.span>
              </p>
            </div>
          </motion.div>

          {/* Main Title - Enhanced */}
          <motion.div
            initial={{ opacity: 0, y: 50, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 1.2, delay: 0.3 }}
            className="mb-8"
          >
            <h1 className="text-7xl md:text-9xl font-display font-bold text-white mb-6 drop-shadow-2xl relative">
              <motion.span
                initial={{ opacity: 0, x: -50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 1, delay: 0.5 }}
                className="inline-block"
              >
                Noor
              </motion.span>
              <motion.span
                initial={{ opacity: 0, scale: 0 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.8, delay: 0.8 }}
                className="inline-block mx-4 text-6xl md:text-8xl"
              >
                &
              </motion.span>
              <motion.span
                initial={{ opacity: 0, x: 50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 1, delay: 0.7 }}
                className="inline-block"
              >
                Priya
              </motion.span>
            </h1>
            
            {/* Decorative Line */}
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: "200px" }}
              transition={{ duration: 1.5, delay: 1.2 }}
              className="h-1 bg-gradient-to-r from-transparent via-white to-transparent mx-auto rounded-full"
            />
          </motion.div>
          
          {/* Subtitle - Enhanced */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.9 }}
            className="mb-12"
          >
            <p className="text-2xl md:text-3xl text-white mb-4 font-light tracking-wider drop-shadow-lg">
              Two hearts, one story.
            </p>
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 1, delay: 1.5 }}
              className="text-lg md:text-xl text-white/90 font-medium"
            >
              A love story that began in high school and continues forever
            </motion.p>
          </motion.div>

          {/* Love Quote - Enhanced */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 30 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.0 }}
            className="mb-16"
          >
            <div className="bg-gradient-to-br from-white/25 to-white/15 backdrop-blur-md rounded-3xl p-8 border border-white/40 max-w-3xl mx-auto shadow-2xl hover:shadow-3xl transition-all duration-300">
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 1, delay: 1.3 }}
                className="text-center"
              >
                <div className="text-4xl mb-4">💕</div>
                <p className="text-white text-xl md:text-2xl italic font-medium drop-shadow-lg leading-relaxed">
                  "In all the world, there is no heart for me like yours. In all the world, there is no love for you like mine."
                </p>
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.8, delay: 1.6 }}
                  className="mt-4 flex items-center justify-center gap-2"
                >
                  <div className="w-8 h-px bg-white/60"></div>
                  <p className="text-white/90 text-sm font-medium">Maya Angelou</p>
                  <div className="w-8 h-px bg-white/60"></div>
                </motion.div>
              </motion.div>
            </div>
          </motion.div>

          {/* Action Buttons - Enhanced */}
          <motion.div
            initial={{ opacity: 0, scale: 0.8, y: 30 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.2 }}
            className="flex flex-col sm:flex-row gap-6 justify-center items-center mb-20"
          >
            <motion.button
              onClick={() => navigate('/our-story')}
              whileHover={{ scale: 1.05, y: -2 }}
              whileTap={{ scale: 0.95 }}
              className="bg-gradient-to-r from-rose-500 to-pink-600 text-white text-lg px-10 py-5 rounded-full font-bold shadow-2xl hover:shadow-3xl transition-all duration-300 border border-white/20 backdrop-blur-sm"
            >
              <span className="flex items-center gap-3">
                <motion.span
                  animate={{ rotate: [0, 10, -10, 0] }}
                  transition={{ duration: 2, repeat: Infinity }}
                >
                  📖
                </motion.span>
                Explore Our Story
              </span>
            </motion.button>
            <motion.button
              onClick={() => window.scrollTo({ top: window.innerHeight * 2, behavior: 'smooth' })}
              whileHover={{ scale: 1.05, y: -2 }}
              whileTap={{ scale: 0.95 }}
              className="bg-white/20 backdrop-blur-md text-white px-10 py-5 rounded-full font-bold hover:bg-white/30 transition-all duration-300 border border-white/40 shadow-xl hover:shadow-2xl"
            >
              <span className="flex items-center gap-3">
                <motion.span
                  animate={{ scale: [1, 1.2, 1] }}
                  transition={{ duration: 2, repeat: Infinity }}
                >
                  📸
                </motion.span>
                View Gallery
              </span>
            </motion.button>
          </motion.div>

          {/* Wedding Highlights - Enhanced */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.4 }}
            className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto"
          >
            {[
              { 
                icon: "💍", 
                title: "The Proposal", 
                description: "Noor asked Priya to be his girlfriend in 2020",
                color: "from-rose-400 to-pink-500",
                delay: 1.6
              },
              { 
                icon: "👰‍♀️", 
                title: "Wedding Day", 
                description: "January 4, 2026 - The day we become one",
                color: "from-purple-400 to-violet-500",
                delay: 1.8
              },
              { 
                icon: "💕", 
                title: "Our Journey", 
                description: "From high school classmates to life partners",
                color: "from-pink-400 to-rose-500",
                delay: 2.0
              }
            ].map((highlight, index) => (
              <motion.div
                key={highlight.title}
                initial={{ opacity: 0, y: 20, scale: 0.8 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                transition={{ duration: 0.6, delay: highlight.delay }}
                whileHover={{ scale: 1.05, y: -5 }}
                className="text-center group cursor-pointer"
              >
                <motion.div
                  whileHover={{ rotate: 360 }}
                  transition={{ duration: 0.6 }}
                  className={`w-20 h-20 mx-auto mb-6 bg-gradient-to-r ${highlight.color} rounded-full flex items-center justify-center text-3xl shadow-lg group-hover:shadow-xl transition-all duration-300`}
                >
                  {highlight.icon}
                </motion.div>
                <h3 className="text-xl font-bold text-white mb-3 drop-shadow-lg">
                  {highlight.title}
                </h3>
                <p className="text-sm text-white/90 font-medium drop-shadow-lg leading-relaxed">
                  {highlight.description}
                </p>
              </motion.div>
            ))}
          </motion.div>
        </div>

       
      </section>
{/* Enhanced Quote Cards Section */}
<section className="py-24 px-4 bg-gradient-to-br from-slate-800 via-slate-900 to-black relative overflow-hidden">
        {/* Background Decorative Elements */}
        <div className="absolute inset-0">
          {[...Array(8)].map((_, i) => (
            <motion.div
              key={`bg-star-${i}`}
              className="absolute text-2xl opacity-20"
              style={{ 
                left: `${5 + i * 12}%`, 
                top: `${10 + (i % 3) * 30}%` 
              }}
              animate={{
                y: [-15, -30, -15],
                rotate: [0, 180, 360],
                opacity: [0.2, 0.4, 0.2],
              }}
              transition={{
                duration: 6,
                repeat: Infinity,
                delay: i * 0.8,
                ease: "easeInOut",
              }}
            >
              ✨
            </motion.div>
          ))}
        </div>

        <div className="max-w-7xl mx-auto relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-20"
          >
            <motion.h2 
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              viewport={{ once: true }}
              className="text-5xl md:text-6xl font-serif font-bold text-white mb-6"
            >
              In Their Own Words
            </motion.h2>
            <motion.p 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              viewport={{ once: true }}
              className="text-xl md:text-2xl text-slate-300 max-w-4xl mx-auto leading-relaxed"
            >
              How Noor and Priya describe their love for each other
            </motion.p>
            
            {/* Decorative Line */}
            <motion.div
              initial={{ width: 0 }}
              whileInView={{ width: "150px" }}
              transition={{ duration: 1, delay: 0.6 }}
              viewport={{ once: true }}
              className="h-1 bg-gradient-to-r from-rose-400 to-violet-400 mx-auto rounded-full mt-6"
            />
          </motion.div>

          <div className="relative overflow-hidden rounded-[36px] bg-gradient-to-r from-slate-900/65 via-slate-900/35 to-slate-900/65 border border-white/10 p-8 md:p-12 shadow-[0_45px_120px_-50px_rgba(15,23,42,0.85)]">
            <div className="absolute inset-0 pointer-events-none">
              <div className="absolute -top-32 -left-24 w-72 h-72 bg-rose-500/30 blur-3xl rounded-full" />
              <div className="absolute -bottom-40 -right-32 w-80 h-80 bg-indigo-500/25 blur-3xl rounded-full" />
              <motion.div
                className="absolute inset-0 opacity-40"
                animate={{ backgroundPosition: ['0% 50%', '100% 50%', '0% 50%'] }}
                transition={{ duration: 30, repeat: Infinity }}
                style={{ backgroundImage: 'radial-gradient(circle at 20% 20%, rgba(255,255,255,0.08) 0, rgba(255,255,255,0.08) 3px, transparent 10px)', backgroundSize: '60px 60px' }}
              />
            </div>

            <div className="relative grid md:grid-cols-[minmax(0,360px)_auto_minmax(0,360px)] gap-10 lg:gap-16 items-center z-10">
              {/* Noor Side */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                whileHover={{ y: -6 }}
                transition={{ duration: 0.55 }}
                className="relative bg-white/95 rounded-[30px] p-8 md:p-9 border border-white/70 shadow-[0_28px_70px_-35px_rgba(244,63,94,0.6)] min-h-[420px] flex flex-col"
              >
                <div className="flex items-start gap-5">
                  <div className="relative">
                    <div className="w-20 h-24 rounded-[26px] overflow-hidden border-2 border-rose-100 shadow-xl rotate-[-4deg]">
                      <picture>
                        <source srcSet="/IMG_8780.heic" type="image/heic" />
                        <img
                          src="/noor-photo.jpg"
                          alt="Noor smiling toward Priya"
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            const target = e.target as HTMLImageElement;
                            target.style.display = 'none';
                            target.parentElement!.innerHTML = '<div class="w-full h-full bg-rose-100 flex items-center justify-center text-4xl text-rose-500">👨‍💻</div>';
                          }}
                        />
                      </picture>
                    </div>
                    <motion.span
                      className="absolute -top-4 -right-4 text-3xl"
                      animate={{ rotate: [-8, 8, -8] }}
                      transition={{ duration: 1.8, repeat: Infinity, ease: 'easeInOut' }}
                    >
                      💻
                    </motion.span>
                  </div>
                  <div>
                    <p className="text-sm uppercase tracking-[0.3em] text-rose-400">Noor</p>
                    <h3 className="text-2xl font-bold text-slate-800 mt-1">Our resident bug whisperer</h3>
                    <p className="text-sm text-slate-500/80 mt-1">The moment he looks at Priya, his inner coder melts.</p>
                  </div>
                </div>

                <div className="relative mt-7">
                  <div className="relative bg-gradient-to-r from-rose-500 via-rose-400 to-pink-500 text-white rounded-3xl px-6 py-5 shadow-[0_20px_45px_-25px_rgba(244,63,94,0.9)]">
                    <p className="text-lg md:text-xl leading-relaxed font-medium italic">
                      “She’s my favorite bug that I never want to fix.”
                    </p>
                    <div className="absolute -right-5 top-1/2 -translate-y-1/2 w-10 h-10 bg-gradient-to-br from-rose-500 to-pink-500 rotate-45 shadow-lg" />
                  </div>
                  <motion.span
                    className="hidden md:block absolute -right-16 top-1/2 -translate-y-1/2 text-6xl drop-shadow-lg"
                    animate={{ x: [0, 8, 0], rotate: [0, -12, 0] }}
                    transition={{ duration: 1.8, repeat: Infinity, ease: 'easeInOut' }}
                  >
                    👉
                  </motion.span>
                </div>

                <div className="mt-6 flex flex-wrap gap-3 text-sm text-rose-500/80">
                  <span className="bg-rose-100/70 px-3 py-1 rounded-full border border-rose-200/70">#DebuggerInLove</span>
                  <span className="bg-rose-100/70 px-3 py-1 rounded-full border border-rose-200/70">#TeamPriya</span>
                  <span className="bg-rose-100/70 px-3 py-1 rounded-full border border-rose-200/70">#NeverFixThisBug</span>
                </div>

                {/* <div className="mt-8 relative pl-8">
                  <div className="absolute left-0 top-1/2 -translate-y-1/2 w-10 h-10 bg-rose-100/80 border border-rose-200/80 rotate-45 rounded-sm shadow-lg" />
                  <div className="relative w-[200px] h-[140px] rounded-[26px] overflow-hidden border-[6px] border-white shadow-[0_20px_40px_-25px_rgba(244,63,94,0.9)] rotate-[-3deg]">
                    <img
                      src="/priya-photo.jpg"
                      alt="Priya smiling back at Noor"
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.style.display = 'none';
                          target.parentElement!.innerHTML = '<div class="w-full h-full bg-rose-200 flex items-center justify-center text-3xl text-rose-500">👑</div>';
                        }}
                    />
                    <div className="absolute bottom-3 right-4 text-xs font-semibold tracking-[0.2em] text-rose-500/80 uppercase">Priya</div>
                  </div>
                </div> */}

                <div className="mt-8">
                  <div className="inline-flex items-center gap-3 bg-rose-100/70 text-rose-600 px-4 py-2 rounded-full border border-rose-200/70 text-sm font-semibold">
                    <span className="text-xl">💖</span>
                    <span>Pointing straight at Priya</span>
                  </div>
                </div>
              </motion.div>

              <div className="hidden md:flex flex-col items-center gap-6 relative">
                <motion.div
                  className="w-16 h-16 rounded-full bg-gradient-to-br from-rose-500 to-pink-500 flex items-center justify-center text-3xl text-white shadow-xl"
                  animate={{ scale: [1, 1.1, 1], rotate: [0, 4, 0] }}
                  transition={{ duration: 2.2, repeat: Infinity, repeatType: 'mirror' }}
                >
                  💑
                </motion.div>
                <motion.div
                  className="w-1 bg-gradient-to-b from-transparent via-white/50 to-transparent rounded-full"
                  animate={{ height: ['120px', '150px', '120px'] }}
                  transition={{ duration: 2.5, repeat: Infinity, repeatType: 'mirror' }}
                />
                <motion.div
                  className="relative w-28 h-28"
                  animate={{ rotate: [0, 360] }}
                  transition={{ duration: 18, repeat: Infinity, ease: 'linear' }}
                >
                  <div className="absolute inset-3 rounded-full border border-white/40" />
                  <div className="absolute inset-0 flex items-center justify-center text-white text-3xl">
                    💞
                  </div>
                </motion.div>
                <motion.div
                  className="w-1 bg-gradient-to-b from-transparent via-white/30 to-transparent rounded-full"
                  animate={{ height: ['150px', '120px', '150px'] }}
                  transition={{ duration: 2.5, repeat: Infinity, repeatType: 'mirror', delay: 0.6 }}
                />
                <motion.div
                  className="w-16 h-16 rounded-full bg-gradient-to-br from-indigo-500 to-violet-500 flex items-center justify-center text-3xl text-white shadow-xl"
                  animate={{ scale: [1, 1.1, 1], rotate: [0, -4, 0] }}
                  transition={{ duration: 2.2, repeat: Infinity, repeatType: 'mirror', delay: 0.7 }}
                >
                  💘
                </motion.div>
              </div>

              <motion.div
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                whileHover={{ y: -6 }}
                transition={{ duration: 0.55 }}
                className="relative bg-white/95 rounded-[30px] p-8 md:p-9 border border-white/70 shadow-[0_28px_70px_-35px_rgba(79,70,229,0.6)]"
              >
                <div className="flex items-start gap-5 justify-end">
                  <div className="text-right">
                    <p className="text-sm uppercase tracking-[0.3em] text-indigo-400">Priya</p>
                    <h3 className="text-2xl font-bold text-slate-800 mt-1">The heart healer on duty</h3>
                    <p className="text-sm text-slate-500/80 mt-1">She already wrote Noor’s chart: permanent patient of love.</p>
                  </div>
                  <div className="relative">
                    <div className="w-20 h-24 rounded-[26px] overflow-hidden border-2 border-indigo-100 shadow-xl rotate-[5deg]">
                      <picture>
                        <source srcSet="/IMG_8780.heic" type="image/heic" />
                        <img
                          src="/noor-photo.jpg"
                          alt="Priya lovingly pointing at Noor"
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            const target = e.target as HTMLImageElement;
                            target.style.display = 'none';
                            target.parentElement!.innerHTML = '<div class="w-full h-full bg-indigo-100 flex items-center justify-center text-4xl text-indigo-600">👩‍⚕️</div>';
                          }}
                        />
                      </picture>
                    </div>
                    <motion.span
                      className="absolute -top-4 -left-4 text-3xl"
                      animate={{ rotate: [8, -8, 8] }}
                      transition={{ duration: 1.8, repeat: Infinity, ease: 'easeInOut', delay: 0.3 }}
                    >
                      🩺
                    </motion.span>
                  </div>
                </div>

                <div className="relative mt-7">
                  <div className="relative bg-gradient-to-l from-indigo-500 via-indigo-400 to-violet-500 text-white rounded-3xl px-6 py-5 shadow-[0_20px_45px_-25px_rgba(79,70,229,0.9)] text-right">
                    <p className="text-lg md:text-xl leading-relaxed font-medium italic">
                      “He’s my favorite patient that I never want to discharge.”
                    </p>
                    <div className="absolute -left-5 top-1/2 -translate-y-1/2 w-10 h-10 bg-gradient-to-br from-indigo-500 to-violet-500 rotate-45 shadow-lg" />
                  </div>
                  <motion.span
                    className="hidden md:block absolute -left-16 top-1/2 -translate-y-1/2 text-6xl drop-shadow-lg"
                    animate={{ x: [0, -8, 0], rotate: [0, 12, 0] }}
                    transition={{ duration: 1.8, repeat: Infinity, ease: 'easeInOut', delay: 0.5 }}
                  >
                    👈
                  </motion.span>
                </div>

                <div className="mt-6 flex flex-wrap gap-3 justify-end text-sm text-indigo-500/80">
                  <span className="bg-indigo-100/70 px-3 py-1 rounded-full border border-indigo-200/70">#DoctorInLove</span>
                  <span className="bg-indigo-100/70 px-3 py-1 rounded-full border border-indigo-200/70">#ForeverPatient</span>
                  <span className="bg-indigo-100/70 px-3 py-1 rounded-full border border-indigo-200/70">#HeartHealer</span>
                </div>

                <div className="mt-8 flex justify-end">
                  <div className="inline-flex items-center gap-3 bg-indigo-100/70 text-indigo-600 px-4 py-2 rounded-full border border-indigo-200/70 text-sm font-semibold">
                    <span>Keeping Noor on her love chart forever</span>
                    <span className="text-xl">💊</span>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>
      {/* Enhanced Couple Photo Banner */}
      <section className="py-24 px-4 bg-gradient-to-br from-rose-50 via-pink-50 to-violet-50 relative overflow-hidden">
        {/* Background Decorative Elements */}
        <div className="absolute inset-0">
          {[...Array(6)].map((_, i) => (
            <motion.div
              key={`bg-heart-${i}`}
              className="absolute text-4xl opacity-10"
              style={{ 
                left: `${10 + i * 15}%`, 
                top: `${20 + (i % 2) * 40}%` 
              }}
              animate={{
                y: [-20, -40, -20],
                rotate: [0, 180, 360],
                opacity: [0.1, 0.2, 0.1],
              }}
              transition={{
                duration: 8,
                repeat: Infinity,
                delay: i * 1.5,
                ease: "easeInOut",
              }}
            >
              💕
            </motion.div>
          ))}
        </div>

        <div className="max-w-7xl mx-auto relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center"
          >
            {/* Section Title */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              viewport={{ once: true }}
              className="mb-12"
            >
              <h2 className="text-4xl md:text-5xl font-serif font-bold text-slate-800 mb-4">
                Our Love Story
              </h2>
              <div className="w-24 h-1 bg-gradient-to-r from-rose-400 to-violet-400 mx-auto rounded-full"></div>
            </motion.div>

            {/* Enhanced Photo Carousel Container */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              viewport={{ once: true }}
              className="relative group"
            >
              <div className="relative overflow-hidden rounded-3xl shadow-2xl group-hover:shadow-3xl transition-all duration-500">
                {/* Decorative Border */}
                <div className="absolute -inset-4 bg-gradient-to-r from-rose-400 via-pink-400 to-violet-400 rounded-3xl opacity-20 group-hover:opacity-30 transition-opacity duration-500 blur-sm"></div>
                
                {/* Carousel Image */}
                <motion.img 
                  key={currentPhotoIndex}
                  src={couplePhotos[currentPhotoIndex].src}
                  alt={couplePhotos[currentPhotoIndex].alt}
                  className="relative w-full h-[500px] md:h-[600px] lg:h-[700px] object-contain bg-gradient-to-br from-rose-100 to-violet-100 group-hover:scale-105 transition-transform duration-700"
                  initial={{ opacity: 0, scale: 1.1 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  transition={{ duration: 0.8, ease: "easeInOut" }}
                  loading="eager"
                  onLoad={(e) => {
                    console.log('Image loaded successfully:', e.target);
                  }}
                  onError={(e) => {
                    console.error('Image failed to load:', e.target);
                    const target = e.target as HTMLImageElement;
                    target.style.display = 'none';
                    target.parentElement!.innerHTML = '<div class="w-full h-[500px] md:h-[600px] lg:h-[700px] bg-gradient-to-br from-rose-400 to-violet-500 flex items-center justify-center text-white text-6xl">💕</div>';
                  }}
                />
                
                {/* Enhanced Overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent" />
                
                {/* Floating Hearts Overlay */}
                <div className="absolute inset-0 pointer-events-none">
                  {[...Array(4)].map((_, i) => (
                    <motion.div
                      key={`overlay-heart-${i}`}
                      className="absolute text-2xl text-white/60"
                      style={{ 
                        left: `${20 + i * 20}%`, 
                        top: `${30 + (i % 2) * 30}%` 
                      }}
                      animate={{
                        y: [-10, -20, -10],
                        opacity: [0.6, 1, 0.6],
                        scale: [1, 1.1, 1],
                      }}
                      transition={{
                        duration: 3,
                        repeat: Infinity,
                        delay: i * 0.8,
                        ease: "easeInOut",
                      }}
                    >
                      💖
                    </motion.div>
                  ))}
                </div>

                {/* Enhanced Text Overlay */}
                <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 text-center text-white">
                  <motion.h3 
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.6 }}
                    viewport={{ once: true }}
                    className="text-3xl md:text-4xl font-serif font-bold mb-2 drop-shadow-lg"
                  >
                    Together Forever
                  </motion.h3>
                  <motion.p 
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.8 }}
                    viewport={{ once: true }}
                    className="text-lg md:text-xl opacity-90 drop-shadow-lg"
                  >
                    Two hearts, one love story
                  </motion.p>
                  
                  {/* Decorative Elements */}
                  <motion.div
                    initial={{ opacity: 0, scale: 0 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.6, delay: 1.0 }}
                    viewport={{ once: true }}
                    className="flex justify-center items-center gap-4 mt-4"
                  >
                    <div className="w-8 h-px bg-white/60"></div>
                    <motion.span
                      animate={{ rotate: [0, 360] }}
                      transition={{ duration: 4, repeat: Infinity, ease: "linear" }}
                      className="text-2xl"
                    >
                      💕
                    </motion.span>
                    <div className="w-8 h-px bg-white/60"></div>
                  </motion.div>
                </div>

                {/* Carousel Indicators */}
                <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex space-x-2 z-20">
                  {couplePhotos.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentPhotoIndex(index)}
                      className={`w-3 h-3 rounded-full transition-all duration-300 ${
                        index === currentPhotoIndex 
                          ? 'bg-white shadow-lg scale-125' 
                          : 'bg-white/50 hover:bg-white/75'
                      }`}
                    />
                  ))}
                </div>

                {/* Carousel Navigation Arrows */}
                <button
                  onClick={() => setCurrentPhotoIndex((prev) => 
                    prev === 0 ? couplePhotos.length - 1 : prev - 1
                  )}
                  className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-white/20 backdrop-blur-sm rounded-full p-3 hover:bg-white/30 transition-all duration-300 z-20"
                >
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
                <button
                  onClick={() => setCurrentPhotoIndex((prev) => 
                    (prev + 1) % couplePhotos.length
                  )}
                  className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-white/20 backdrop-blur-sm rounded-full p-3 hover:bg-white/30 transition-all duration-300 z-20"
                >
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      

      {/* Time Together Section */}
      <section className="relative py-20 px-4 bg-gradient-to-br from-rose-50 to-pink-50">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 50 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="max-w-6xl mx-auto text-center"
        >
          <motion.h2 
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            viewport={{ once: true }}
            className="text-4xl md:text-5xl font-serif font-bold text-slate-800 mb-4"
          >
            Time Together
          </motion.h2>
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            viewport={{ once: true }}
            className="text-xl text-slate-600 mb-12"
          >
            Since September 5, 2020 at 3:00 PM
          </motion.p>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 max-w-6xl mx-auto">
            {[
              { label: 'Years', value: timeTogether.years, icon: '💕' },
              { label: 'Months', value: timeTogether.months, icon: '📅' },
              { label: 'Days', value: timeTogether.days, icon: '📆' },
              { label: 'Hours', value: timeTogether.hours, icon: '⏰' },
              { label: 'Minutes', value: timeTogether.minutes, icon: '⏱️' },
              { label: 'Seconds', value: timeTogether.seconds, icon: '💫' },
            ].map((unit, index) => (
              <motion.div
                key={unit.label}
                initial={{ opacity: 0, scale: 0.8, y: 20 }}
                whileInView={{ opacity: 1, scale: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.6 + index * 0.1 }}
                viewport={{ once: true }}
                whileHover={{ scale: 1.05, y: -5 }}
                className="bg-white/90 backdrop-blur-sm rounded-2xl p-4 shadow-lg border border-white/90 hover:shadow-xl transition-all duration-300"
              >
                <motion.div
                  animate={{ rotate: [0, 5, -5, 0] }}
                  transition={{ duration: 2, repeat: Infinity, delay: index * 0.3 }}
                  className="text-2xl mb-2"
                >
                  {unit.icon}
                </motion.div>
                <div className="text-3xl md:text-4xl font-bold text-rose-600 mb-1">
                  {String(unit.value).padStart(2, '0')}
                </div>
                <div className="text-sm font-medium text-slate-700">
                  {unit.label}
                </div>
              </motion.div>
            ))}
          </div>
          
          {/* Love Message */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.2 }}
            viewport={{ once: true }}
            className="mt-12 bg-gradient-to-r from-rose-100 to-pink-100 rounded-2xl p-6 max-w-4xl mx-auto"
          >
            <p className="text-lg md:text-xl text-slate-700 italic">
              "Every second, every minute, every hour, every day has been a blessing since we became one."
            </p>
            <div className="flex justify-center items-center gap-2 mt-4">
              <motion.span
                animate={{ rotate: [0, 360] }}
                transition={{ duration: 3, repeat: Infinity, ease: "linear" }}
                className="text-2xl"
              >
                💕
              </motion.span>
              <span className="text-slate-600 font-medium">Noor & Priya</span>
              <motion.span
                animate={{ rotate: [0, 360] }}
                transition={{ duration: 3, repeat: Infinity, ease: "linear" }}
                className="text-2xl"
              >
                💕
              </motion.span>
            </div>
          </motion.div>
        </motion.div>
      </section>

      {/* Meet the Couple Section */}
      <section className="py-20 px-4 bg-gradient-to-br from-rose-50 to-violet-50 relative overflow-hidden">
        {/* Floating Hearts Background - Simplified */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {[...Array(3)].map((_, i) => (
            <motion.div
              key={`floating-heart-${i}`}
              className="absolute text-xl opacity-5"
              style={{ 
                left: `${20 + i * 30}%`, 
                top: `${30 + (i % 2) * 40}%`
              }}
              animate={{
                y: [-5, -15, -5],
                rotate: [0, 5, -5, 0],
              }}
              transition={{
                duration: 8 + i * 2,
                repeat: Infinity,
                delay: i * 1.5,
                ease: "easeInOut",
              }}
            >
              💕
            </motion.div>
          ))}
        </div>

        <div className="max-w-7xl mx-auto relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-5xl md:text-6xl font-great-vibes text-slate-800 mb-6">
              Meet the Amazing Couple
            </h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto leading-relaxed">
              Two goofballs who somehow found each other in this crazy world. 
              <br />One gets lost, the other eats everything. It's a match made in heaven! 😄
            </p>
          </motion.div>

          <div className="grid lg:grid-cols-2 gap-12">
            {/* Noor's Card - Travel Lover */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={inView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.8, delay: 0.2 }}
              whileHover={{ scale: 1.02, y: -10 }}
              className="group"
            >
              <Link to="/noor" className="block">
                <div className="bg-gradient-to-br from-cyan-500 via-blue-600 to-indigo-700 rounded-3xl p-8 text-white shadow-2xl hover:shadow-3xl transition-all duration-500 relative overflow-hidden">
                  {/* Travel Pattern Background */}
                  <div className="absolute top-0 right-0 w-32 h-32 opacity-10">
                    <div className="text-6xl">✈️</div>
                  </div>
                  
                  <div className="relative z-10">
                    <div className="text-center mb-8">
                      <div className="w-36 h-36 mx-auto mb-6 rounded-full overflow-hidden border-4 border-white/30 group-hover:scale-110 transition-transform duration-300 shadow-xl">
                        <img 
                          src="/noor-photo.jpg" 
                          alt="Noor" 
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            const target = e.target as HTMLImageElement;
                            target.style.display = 'none';
                            target.parentElement!.innerHTML = '<div class="w-full h-full bg-white/20 rounded-full flex items-center justify-center text-5xl">🧳</div>';
                          }}
                        />
                      </div>
                      <h3 className="text-4xl font-bold mb-3">Noor</h3>
                      <p className="text-cyan-200 text-xl font-medium">The Travel Enthusiast</p>
                    </div>

                    <div className="space-y-4">
                      <div className="bg-white/15 rounded-2xl p-6 border border-white/25 backdrop-blur-sm">
                        <p className="text-cyan-100 text-center italic text-xl font-medium">
                          "I got lost in my own city once... for 3 hours"
                        </p>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-4">
                        <div className="bg-white/10 rounded-xl p-4 text-center group-hover:bg-white/20 transition-colors duration-300">
                          <div className="text-3xl mb-2">🗺️</div>
                          <p className="text-cyan-100 text-sm font-medium">GPS Dependent</p>
                        </div>
                        <div className="bg-white/10 rounded-xl p-4 text-center group-hover:bg-white/20 transition-colors duration-300">
                          <div className="text-3xl mb-2">📸</div>
                          <p className="text-cyan-100 text-sm font-medium">Over-Photographer</p>
                        </div>
                        <div className="bg-white/10 rounded-xl p-4 text-center group-hover:bg-white/20 transition-colors duration-300">
                          <div className="text-3xl mb-2">🏄‍♂️</div>
                          <p className="text-cyan-100 text-sm font-medium">Water Sports</p>
                        </div>
                        <div className="bg-white/10 rounded-xl p-4 text-center group-hover:bg-white/20 transition-colors duration-300">
                          <div className="text-3xl mb-2">🌍</div>
                          <p className="text-cyan-100 text-sm font-medium">New Countries</p>
                        </div>
                      </div>
                      
                      <div className="bg-gradient-to-r from-white/20 to-white/10 rounded-xl p-4 text-center">
                        <p className="text-cyan-100 font-medium">Fun Fact: Can't parallel park to save his life! 🚗💥</p>
                      </div>
                    </div>

                    <div className="mt-8 text-center">
                      <span className="inline-block bg-white/25 px-6 py-3 rounded-full text-sm font-medium group-hover:bg-white/35 transition-colors duration-300 shadow-lg">
                        Discover Noor's World →
                      </span>
                    </div>
                  </div>
                </div>
              </Link>
            </motion.div>

            {/* Priya's Card - Food Lover */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={inView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.8, delay: 0.4 }}
              whileHover={{ scale: 1.02, y: -10 }}
              className="group"
            >
              <Link to="/priya" className="block">
                <div className="bg-gradient-to-br from-orange-500 via-pink-600 to-rose-700 rounded-3xl p-8 text-white shadow-2xl hover:shadow-3xl transition-all duration-500 relative overflow-hidden">
                  {/* Food Pattern Background */}
                  <div className="absolute top-0 right-0 w-32 h-32 opacity-10">
                    <div className="text-6xl">🍽️</div>
                  </div>
                  
                  <div className="relative z-10">
                    <div className="text-center mb-8">
                      <div className="w-36 h-36 mx-auto mb-6 rounded-full overflow-hidden border-4 border-white/30 group-hover:scale-110 transition-transform duration-300 shadow-xl">
                        <img 
                          src="/priya-photo.jpg" 
                          alt="Priya" 
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            const target = e.target as HTMLImageElement;
                            target.style.display = 'none';
                            target.parentElement!.innerHTML = '<div class="w-full h-full bg-white/20 rounded-full flex items-center justify-center text-5xl">🍴</div>';
                          }}
                        />
                      </div>
                      <h3 className="text-4xl font-bold mb-3">Priya</h3>
                      <p className="text-orange-200 text-xl font-medium">The Food Connoisseur</p>
                    </div>

                    <div className="space-y-4">
                      <div className="bg-white/15 rounded-2xl p-6 border border-white/25 backdrop-blur-sm">
                        <p className="text-orange-100 text-center italic text-xl font-medium">
                          "I can eat eggs for breakfast, lunch, and dinner... and I do!"
                        </p>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-4">
                        <div className="bg-white/10 rounded-xl p-4 text-center group-hover:bg-white/20 transition-colors duration-300">
                          <div className="text-3xl mb-2">🥟</div>
                          <p className="text-orange-100 text-sm font-medium">Golgappe Addict</p>
                        </div>
                        <div className="bg-white/10 rounded-xl p-4 text-center group-hover:bg-white/20 transition-colors duration-300">
                          <div className="text-3xl mb-2">🍝</div>
                          <p className="text-orange-100 text-sm font-medium">Pasta Obsessed</p>
                        </div>
                        <div className="bg-white/10 rounded-xl p-4 text-center group-hover:bg-white/20 transition-colors duration-300">
                          <div className="text-3xl mb-2">🧀</div>
                          <p className="text-orange-100 text-sm font-medium">Shahi Paneer</p>
                        </div>
                        <div className="bg-white/10 rounded-xl p-4 text-center group-hover:bg-white/20 transition-colors duration-300">
                          <div className="text-3xl mb-2">☕</div>
                          <p className="text-orange-100 text-sm font-medium">Chai Addict</p>
                        </div>
                      </div>
                      
                      <div className="bg-gradient-to-r from-white/20 to-white/10 rounded-xl p-4 text-center">
                        <p className="text-orange-100 font-medium">Fun Fact: Can eat 20 golgappes in one sitting! 🥟💪</p>
                      </div>
                    </div>

                    <div className="mt-8 text-center">
                      <span className="inline-block bg-white/25 px-6 py-3 rounded-full text-sm font-medium group-hover:bg-white/35 transition-colors duration-300 shadow-lg">
                        Explore Priya's Kitchen →
                      </span>
                    </div>
                  </div>
                </div>
              </Link>
            </motion.div>
          </div>

          {/* Perfect Match Section */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.6 }}
            className="mt-16 text-center"
          >
            <div className="bg-gradient-to-r from-rose-100 to-pink-100 rounded-3xl p-8 border border-rose-200/50">
              <h3 className="text-3xl font-great-vibes text-slate-800 mb-4">
                The Perfect Match
              </h3>
              <p className="text-lg text-slate-600 max-w-2xl mx-auto mb-4">
                He gets lost everywhere, she knows where all the good food is. 
                <br />He takes 1000 photos, she eats them all. 
                <br />Together, they're the ultimate adventure team! 
                <span className="text-2xl ml-2">💕</span>
              </p>
              <div className="flex justify-center space-x-4 text-2xl">
                <span>🗺️</span>
                <span>+</span>
                <span>🍕</span>
                <span>=</span>
                <span>💕</span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Video Section */}
      <VideoSection />

      {/* Preview Section */}
      <section className="relative py-20 px-4 bg-white/30">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-serif font-bold text-slate-800 mb-6">
              Our Journey Together
            </h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              From our first meeting to this beautiful day, every moment has been a step closer to forever.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                title: 'Our Story',
                description: 'Discover the beautiful journey that brought us together',
                icon: '💕',
                link: '/our-story'
              },
              {
                title: 'Gallery',
                description: 'Cherished moments captured in time',
                icon: '📸',
                link: '/gallery'
              },
              {
                title: 'Love Notes',
                description: 'Sweet messages from our hearts to yours',
                icon: '💌',
                link: '/love-notes'
              },
            ].map((item, index) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
                viewport={{ once: true }}
                className="card-elegant text-center group cursor-pointer"
                onClick={() => window.location.href = item.link}
              >
                <div className="text-6xl mb-6 group-hover:scale-110 transition-transform duration-300">
                  {item.icon}
                </div>
                <h3 className="text-2xl font-serif font-bold text-slate-800 mb-4">
                  {item.title}
                </h3>
                <p className="text-slate-600">
                  {item.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Background Gradient */}
      <div className="fixed inset-0 bg-gradient-to-br from-rose-200/20 via-violet-200/20 to-emerald-200/20 pointer-events-none" />
    </div>
  );
};

export default WeddingHome;
